import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { GetIndicatorsRequest, SalesInsightsResponse, useSalesInsights } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateTimeParam } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import NumericChart from '../charts/numericChart/NumericChart';

const SalesInsightAreaHospitality = () => {
    const { t } = useTranslation();
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);

    const [params, setParams] = useState<GetIndicatorsRequest>({
        fields: 'totalSales,totalTax,netSales,transactionVolume,avgTransactionValue,activeStaff,activeDevices,totalDiscount,totalServiceCharge,totalBasketDiscount,totalCustomerDiscount,totalAmountPaidByPoints,totalItemDiscount,refunds,netMargin',
        startDateTime: formatDateTimeParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
        endDateTime: formatDateTimeParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
        locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id
    });

    const {
        data: salesInsights,
        isLoading,
        isError,
        refetch: fetchSalesInsights
    } = useSalesInsights<SalesInsightsResponse>(params, 'sales-insights');

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesInsights();
        }
    }, [refreshDate]);

    let salesPercentage: number | undefined;
    if (salesInsights?.salesInsights?.totalTax && salesInsights?.salesInsights?.totalSalesIncTax
        && salesInsights?.salesInsights?.totalSalesIncTax !== 0
    ) {
        salesPercentage = (salesInsights.salesInsights.totalTax / salesInsights.salesInsights.totalSalesIncTax);
    }

    let refundPercentage: number | undefined;
    if (salesInsights?.salesInsights?.totalDiscount && salesInsights?.salesInsights?.totalSalesIncTax
        && salesInsights?.salesInsights?.totalSalesIncTax !== 0
    ) {
        refundPercentage = (salesInsights.salesInsights.totalDiscount / salesInsights.salesInsights.totalSalesIncTax);
    }

    const totalDiscount = salesInsights?.salesInsights?.totalDiscount ?? 0;

    let customerDiscountPercentage: number | undefined;
    const totalCustomerDiscount = salesInsights?.salesInsights?.totalCustomerDiscount;

    if (
        totalDiscount !== undefined &&
        totalCustomerDiscount !== undefined &&
        totalDiscount !== 0
    ) {
        customerDiscountPercentage = (totalCustomerDiscount / totalDiscount);
    }

        let itemDiscountPercentage: number | undefined;
    const totalItemDiscount = salesInsights?.salesInsights.totalItemDiscount;

    if (
        totalDiscount !== undefined &&
        totalItemDiscount !== undefined &&
        totalDiscount !== 0
    ) {
        itemDiscountPercentage = (totalItemDiscount / totalDiscount);
    }

    useEffect(() => {
        setParams({
            ...params,
            startDateTime: filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
            endDateTime: filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
            locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id
        });
    }, [filters]);

    useEffect(() => { fetchSalesInsights(); }, [params]);

    return (
        <Box sx={{ marginY: '20px', width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent:'space-evenly' }}>
                <NumericChart
                    title={t("screens.dashboarding.charts.sales")}
                    subtitle={t("screens.dashboarding.charts.includingTax")}
                    alignment="double"
                    mainNumber={salesInsights?.salesInsights.totalSalesIncTax}
                    mainCurrency={salesInsights?.currency}
                    numbers={[
                        {
                            number: salesInsights?.salesInsights.totalTax,
                            description: t("screens.dashboarding.charts.totalTax"),
                            currency: salesInsights?.currency
                        },
                        {
                            number: salesPercentage,
                            description: t("screens.dashboarding.charts.overall"),
                            currency: salesInsights?.currency,
                            showAsPercentage: true
                        }
                    ]}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                    buttonText={t("screens.dashboarding.charts.salesOverview")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.transactions")}
                    subtitle={t("screens.dashboarding.charts.volume")}
                    alignment="single"
                    mainNumber={salesInsights?.salesInsights.transactionVolume}
                    numbers={[
                        {
                            number: salesInsights?.salesInsights.avgTransactionValue,
                            description: `${t("screens.dashboarding.charts.averageNetSales")} ${t("screens.dashboarding.charts.incTax")}`,
                            currency: salesInsights?.currency
                        }
                    ]}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_OAUTH_CALLBACK_URL}transactions`}
                    buttonText={t("screens.dashboarding.charts.transactionsOverview")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.netSales")}
                    subtitle={t("screens.dashboarding.charts.excludingTax")}
                    alignment="empty"
                    mainNumber={salesInsights?.salesInsights.netSalesExcTax}
                    mainCurrency={salesInsights?.currency}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                    buttonText={t("screens.dashboarding.charts.salesOverview")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.netMargin")}
                    subtitle={t("screens.dashboarding.charts.excludingTax")}
                    alignment="single"
                    mainNumber={salesInsights?.salesInsights.netMargin}
                    mainCurrency={salesInsights?.currency}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                    buttonText={t("screens.dashboarding.charts.salesOverview")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.discounts")}
                    subtitle={t("screens.dashboarding.charts.includingTax")}
                    alignment="quad"
                    mainNumber={salesInsights?.salesInsights.totalDiscount}
                    mainCurrency={salesInsights?.currency}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/PromotionReport.aspx`}
                    buttonText={t("screens.dashboarding.charts.promotionsReport")}
                />

                <NumericChart
                    title={t("screens.dashboarding.charts.refunds")}
                    subtitle={t("screens.dashboarding.charts.includingTax")}
                    alignment="double"
                    mainNumber={salesInsights?.salesInsights.refundVolume}
                    mainCurrency={salesInsights?.currency}
                    numbers={[
                        {
                            number: salesInsights?.salesInsights.totalRefundAmount,
                            description: t("screens.dashboarding.charts.totalRefunds"),
                            currency: salesInsights?.currency
                        },
                        {
                            number: refundPercentage,
                            description: t("screens.dashboarding.charts.overall"),
                            currency: salesInsights?.currency,
                            showAsPercentage: true
                        }
                    ]}
                    isError={isError}
                    isLoading={isLoading}
                />


                <NumericChart
                    title={t("screens.dashboarding.charts.discounts")}
                    subtitle={t("screens.dashboarding.charts.basket")}
                    alignment="quad"
                    mainNumber={salesInsights?.salesInsights.totalItemDiscount}
                    mainCurrency={salesInsights?.currency}
                     numbers={[
                        {
                            number: salesInsights?.salesInsights.totalCustomerDiscount,
                            description: t("screens.dashboarding.charts.customerDiscount"),
                            currency: salesInsights?.currency
                        },
                        {
                            number: customerDiscountPercentage,
                            description: t("screens.dashboarding.charts.overall"),
                            currency: salesInsights?.currency,
                            showAsPercentage: true
                        },
                        {
                            number: salesInsights?.salesInsights.totalItemDiscount,
                            description: t("screens.dashboarding.charts.discounts"),
                            currency: salesInsights?.currency
                        },
                                                {
                            number: itemDiscountPercentage,
                            description: t("screens.dashboarding.charts.overall"),
                            showAsPercentage: true
                        },
                    ]}
                    isError={isError}
                    isLoading={isLoading}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/PromotionReport.aspx`}
                    buttonText={t("screens.dashboarding.charts.promotionsReport")}
                />
            </Box>
        </Box>
    )
}

export default SalesInsightAreaHospitality;
